import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

export enum TaskType {
  text = 'text',
  link = 'link',
  test = 'test',
  exclude = 'exclude',
  selectOne = 'selectOne',
  sentenceStructure = 'sentenceStructure',
  gap = 'gap',
  mathFormulas = 'mathFormulas',
  wordComposition = 'wordComposition',
  wordStress = 'wordStress',
  underline = 'underline',
}

// Task
export const taskSchema = yup.object().shape({
  id: yup.string().required('Required'),
  text: yup.string().required('Required'),
  subText: yup.string(),
  type: yup
    .mixed<TaskType>()
    .oneOf(Object.values(TaskType).map(e => e as TaskType))
    .defined()
    .required('Required'),
  completedAt: yup.string(),
  checkedAt: yup.string(),
  isActive: yup.boolean().required('Required'),
  createdAt: yup.number().required('Required'),
  indexNumber: yup.number().required('Required'),
  order: yup.number().optional(),
  hasAnswer: yup.boolean(),
  hasStar: yup.boolean(),
  hasPhoto: yup.boolean(),
  deadline: yup.string(),
  comments: yup.array().of(
    yup.object().shape({
      id: yup.string().required('Required'),
      text: yup.string().required('Required'),
      isViewed: yup.boolean().required('Required'),
      date: yup.date().required('Required'),
    }),
  ),
  answer: yup.string(),
  answersPhotos: yup.array().of(yup.string().required('Required')),
  editedAnswerPhotos: yup.array().of(yup.string().required('Required')),
  taskPhotos: yup.array().of(yup.string().required('Required')),

  audioUrl: yup.string(),
  videoUrl: yup.string(),
  pdfUrl: yup.string(),
  wordUrl: yup.string(),
  studentPdfUrl: yup.string(),
  studentWordUrl: yup.string(),

  // exam functionality
  mark: yup.number().min(1, 'minimal mark').optional(),
  grade: yup.number().optional(),
  maxGrade: yup.number().optional().nullable(),
  points: yup.number().min(1, 'minimal mark').optional(),
  teacherPoints: yup.number().min(0, 'minimal mark').optional(),
  isCorrectAnswer: yup.boolean(),
});
export type Task = yup.InferType<typeof taskSchema>;

// ---------------------------------------------- Task With Link Task --------------------------------------------------
export const taskWithLinkSchema = taskSchema.shape({
  url: yup.string().url().required('Required link'),
});
export type TaskWithLinkType = yup.InferType<typeof taskWithLinkSchema>;

// ---------------------------------------------- Text Task --------------------------------------------------
export const textTaskSchema = taskSchema.shape({});
export type TextTask = yup.InferType<typeof textTaskSchema>;

// ---------------------------------------------- Test Task --------------------------------------------------
export const testItemFormSchema = yup.object().shape({
  text: yup.string().required('Required'),
  isRight: yup.boolean().required('Required'),
});
export type TestItemForm = yup.InferType<typeof testItemFormSchema>;

export const testItemSchema = testItemFormSchema.shape({
  id: yup.string().required('Required'),
  answer: yup.boolean(),
});
export type TestItem = yup.InferType<typeof testItemSchema>;

export const testTaskSchema = taskSchema.shape({
  testItems: yup.array().of(testItemSchema.required('Required')).min(1, 'more then one option').defined(),
});
export type TestTask = yup.InferType<typeof testTaskSchema>;

// ---------------------------------------------- Gap Task --------------------------------------------------
export const gapWordSchema = yup.object().shape({
  id: yup.string().required('Required'),
  word: yup.string().required('Required'),
  answer: yup.string().optional(),
  correctAnswer: yup.string().optional(),
});
export type GapWord = yup.InferType<typeof gapWordSchema>;

export enum GapTaskTypes {
  words = 'words',
  letters = 'letters',
}

export const gapTaskSchema = taskSchema.shape({
  gapWords: yup.array().of(gapWordSchema.required('Required')).defined(),
  gapTaskType: yup
    .mixed<GapTaskTypes>()
    .oneOf(Object.values(GapTaskTypes).map(e => e as GapTaskTypes))
    .defined()
    .required('Required'),
  sentence: yup.string().required('Required'),
});
export type GapTask = yup.InferType<typeof gapTaskSchema>;

// ---------------------------------------------- Math Formulas Task --------------------------------------------------

export const formulaFormSchema = yup.object().shape({
  formula: yup.string().required('Required'),
});

export const formulaSchema = formulaFormSchema.shape({
  id: yup.string().required('Required'),
  answer: yup.string(),
});
export type Formula = yup.InferType<typeof formulaSchema>;

export const mathFormulasTaskSchema = taskSchema.shape({
  formulas: yup.array().of(formulaSchema.required('Required')).min(1, 'more then one formula').defined(),
});
export type MathFormulasTask = yup.InferType<typeof mathFormulasTaskSchema>;

// ---------------------------------------------- Word Composition Task --------------------------------------------------
export const wordLettersToWorkSchema = yup.object().shape({
  id: yup.string().required('Required'),
  letter: yup.string().required('Required'),
  bgImg: yup.string(),
});
export type WordLettersToWork = yup.InferType<typeof wordLettersToWorkSchema>;

export const wordLettersToShowSchema = yup.object().shape({
  id: yup.string().required('Required'),
  letters: yup.string().required('Required'),
  bgImg: yup.string(),
});
export type WordLettersToShow = yup.InferType<typeof wordLettersToShowSchema>;

export const wordCompositionSchema = yup.object().shape({
  id: yup.string().required('Required'),
  word: yup.string().required('Required'),
  wordLettersToWork: yup.array().of(wordLettersToWorkSchema.required('Required')).defined(),
  wordLettersToShow: yup.array().of(wordLettersToShowSchema.required('Required')),
  numberOfLettersToShow: yup.number(),
});
export type WordComposition = yup.InferType<typeof wordCompositionSchema>;

export const wordCompositionTaskSchema = taskSchema.shape({
  words: yup.array().of(wordCompositionSchema.required('Required')).min(1, 'Required').defined(),
});
export type WordCompositionTask = yup.InferType<typeof wordCompositionTaskSchema>;

// ---------------------------------------------- Word Stress Task --------------------------------------------------
export const wordStressFormSchema = yup.object().shape({
  word: yup.string().required('Required'),
});

export const wordStressSchema = wordStressFormSchema.shape({
  id: yup.string().required('Required'),
  word: yup.string().required('Required'),
  wordLettersToWork: yup.array().of(wordLettersToWorkSchema.required('Required')).defined(),
});
export type WordStress = yup.InferType<typeof wordStressSchema>;

export const wordStressTaskSchema = taskSchema.shape({
  words: yup.array().of(wordStressSchema.required('Required')).min(1, 'Required').defined(),
});
export type WordStressTask = yup.InferType<typeof wordStressTaskSchema>;

// ---------------------------------------------- Underline Task --------------------------------------------------
export const underlineWordFormSchema = yup.object().shape({
  word: yup.string().required('Required'),
});

export const underlineWordSchema = underlineWordFormSchema.shape({
  id: yup.string().required('Required'),
  isUnderlined: yup.boolean(),
});
export type UnderlineWord = yup.InferType<typeof underlineWordSchema>;

export const underlineTaskSchema = taskSchema.shape({
  words: yup.array().of(underlineWordSchema.required('Required')).min(1, 'Required').defined(),
});
export type UnderlineTask = yup.InferType<typeof underlineTaskSchema>;

// ---------------------------------------------- Excludes Task --------------------------------------------------
export const excludeOptionSchema = yup.object().shape({
  id: yup.string().required('Required'),
  words: yup.array().of(yup.string().required('Required')).min(1, 'more then one word').defined(),
  excludeWords: yup.array().of(yup.string().required('Required')).defined(),
});
export type ExcludeOption = yup.InferType<typeof excludeOptionSchema>;

export const excludeAnswerSchema = yup.object().shape({
  lineId: yup.string().required('Required'),
  words: yup.array().of(yup.string().required('Required')).defined(),
});
export type ExcludeAnswer = yup.InferType<typeof excludeAnswerSchema>;

export const excludesTaskSchema = taskSchema.shape({
  options: yup.array().of(excludeOptionSchema.required('Required')).min(1, 'more then one line').defined(),
  answerOptions: yup.array().of(excludeAnswerSchema.required('Required')),
});
export type ExcludeTask = yup.InferType<typeof excludesTaskSchema>;

// ---------------------------------------------- Select One Task --------------------------------------------------
export const selectOneTaskLineSchema = yup.object().shape({
  words: yup
    .array()
    .of(
      yup.lazy(val => {
        if (typeof val !== 'string')
          return yup.object().shape({
            description: yup.string().required().min(1, 'Required'),
            first: yup.string().required().min(1, 'Required'),
            second: yup.string().required().min(1, 'Required'),
            firstCorrect: yup
              .boolean()
              .defined()
              .nullable()
              .test('need-select', 'need select one of options', val => val !== null),
          });
        else return yup.string().required().min(1, 'Required');
      }),
    )
    .defined(),
  id: yup.string().required(),
  description: yup.string().required().notRequired(), // make description optional for flexibility
  descriptionAfter: yup.string().nullable().notRequired(),
});

export type SelectOneTaskLine = yup.InferType<typeof selectOneTaskLineSchema>;

export const selectOneTaskFormRegex = /(?<description>.*?)\(\s*(?<first>[^/]+)\s*\/\s*(?<second>[^\)]+)\s*\)/;

export const selectOneTaskLineFormSchema = yup.object().shape({
  line: yup
    .string()
    .trim()
    .matches(
      /\(\s*[\p{L}0-9]+(?:\s*[\p{L}0-9]+)*\s*\/\s*[\p{L}0-9]+(?:\s*[\p{L}0-9]+)*\s*\)/u,
      'Invalid format. Make sure to include (First option / Second option) somewhere in the sentence',
    )
    .required('Required'),
});

export type SelectOneTaskLineForm = yup.InferType<typeof selectOneTaskLineFormSchema>;

export function convertStringLineToObjectLine(val: string): SelectOneTaskLine & { descriptionAfter?: string } {
  const output: SelectOneTaskLine = {
    id: uuidv4(),
    words: [],
    description: '',
    descriptionAfter: '',
  };

  const regex = /\(\s*(?<first>[^/]+)\s*\/\s*(?<second>[^\)]+)\s*\)/;
  const match = val.match(regex);

  if (match?.groups) {
    const { first, second } = match.groups;
    const [before, after] = val.split(match[0]);

    output.description = before.trim(); // descriptionBefore
    output.descriptionAfter = after?.trim() || ''; // опис після варіанта
    output.words.push({
      description: match[0],
      first: first.trim(),
      second: second.trim(),
      firstCorrect: null,
    });
  } else {
    output.description = val.trim(); // якщо немає варіантів
  }

  return output as SelectOneTaskLine & { descriptionAfter?: string };
}

export const selectOneAnswerSchema = yup.object().shape({
  lineId: yup.string().required('Required'),
  wordId: yup.number().required('Required'),
  firstCorrect: yup.boolean().required('Required'),
});
export type SelectOneAnswer = yup.InferType<typeof selectOneAnswerSchema>;

export const selectOneTaskSchema = taskSchema.shape({
  lines: yup.array().of(selectOneTaskLineSchema.required('Required')).min(1, 'Required').defined(),
  linesAnswer: yup.array().of(selectOneAnswerSchema.required('Required')),
});
export type SelectOneTask = yup.InferType<typeof selectOneTaskSchema>;

// ---------------------------------------------- Sentence Structure Task --------------------------------------------------
export enum WordOrderPartEnum {
  subject = 'subject', // ———— підмет
  predicate = 'predicate', // ===== присудок
  definition = 'definition', // 〰〰〰 означення
  addition = 'addition', // ----- додаток
  circumstance = 'circumstance', // _._._._ обставина
  clear = '',
}

export const sentencePartSchema = yup.object().shape({
  word: yup.string().required('Required'),
  part: yup
    .mixed<WordOrderPartEnum>()
    .oneOf(Object.values(WordOrderPartEnum).map(e => e as WordOrderPartEnum))
    .defined()
    .required('Required'),
  index: yup.number().required('Required'),
});
export type SentencePart = yup.InferType<typeof sentencePartSchema>;

export const sentenceStructureTaskSchema = taskSchema.shape({
  sentence: yup.string().required('Required'),
  sentenceParts: yup.array().of(sentencePartSchema.defined()),
});
export type SentenceStructureTask = yup.InferType<typeof sentenceStructureTaskSchema>;

// ---------------------------------------------- OTHER --------------------------------------------------
export const checkListCreateSchema = yup.object().shape({
  theme: yup.string().max(60, 'Too long, 60 max').required('Required'),
  checkListNumber: yup
    .number()
    .max(99999, 'Too long, 5 max')
    .typeError('Must be a number')
    .positive('Must be positive')
    .required('Required'),
  courseGoal: yup.string().max(120, 'Too long, 120 max').required('Required'),
  courseName: yup.string().max(30, 'Too long, 30 max').required('Required'),
  studentClass: yup.string().required('Required'),
  language: yup.string().required('Required'),
  subject: yup
    .object()
    .shape({
      id: yup.string().required('Required'),
      name: yup.string().required('Required'),
      ruName: yup.string().required('Required'),
    })
    .required('Required'),
});

export enum WordPart {
  prefix = 'prefix',
  suffix = 'suffix',
  root = 'root',
  ending = 'ending',
}

export interface EmptyTasksType {
  text: TextTask;
  link: TaskWithLinkType;
  test: TestTask;
  exclude: ExcludeTask;
  selectOne: SelectOneTask;
  sentenceStructure: SentenceStructureTask;
  gap: GapTask;
  mathFormulas: MathFormulasTask;
  wordComposition: WordCompositionTask;
  wordStress: WordStressTask;
  underline: UnderlineTask;
}

export type TaskKeysType =
  | keyof TextTask
  | keyof TaskWithLinkType
  | keyof ExcludeTask
  | keyof SelectOneTask
  | keyof TestTask
  | keyof SentenceStructureTask
  | keyof GapTask
  | keyof WordCompositionTask
  | keyof WordStressTask
  | keyof UnderlineTask
  | keyof MathFormulasTask;

export type SummarizeTask = Task;
